import {
  collection,
  doc,
  setDoc,
  getDocs,
  query,
  orderBy,
  limit,
} from 'firebase/firestore';
import { db } from '../firebase/config/firebase';
import { AuditLog, AuditAction } from '../types';

const STORAGE_KEY = 'oto_dev_audit_logs';

function getLocalAuditLogs(): AuditLog[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: AuditLog[] = JSON.parse(raw);
    const clean = Array.isArray(parsed)
      ? parsed.filter((l) => l && !['log_01', 'log_02', 'log_03', 'log_04'].includes(l.id))
      : [];
    if (clean.length !== (Array.isArray(parsed) ? parsed.length : 0)) {
      saveLocalAuditLogs(clean);
    }
    return clean;
  } catch {
    return [];
  }
}

function saveLocalAuditLogs(list: AuditLog[]) {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {
    // Ignored
  }
}

export const auditService = {
  /**
   * Log an administrative action
   */
  async logAction(
    adminUid: string,
    adminEmail: string,
    action: AuditAction,
    resourceType: AuditLog['resourceType'],
    resourceId: string,
    details?: Record<string, unknown>
  ): Promise<boolean> {
    try {
      const nowIso = new Date().toISOString();
      const logRef = doc(collection(db, 'auditLogs'));
      const logEntry: AuditLog = {
        id: logRef.id,
        adminUid,
        adminEmail,
        action,
        resourceType,
        resourceId,
        details: details || {},
        timestamp: nowIso,
      };

      try {
        await setDoc(logRef, logEntry);
      } catch (err) {
        console.warn('Could not write audit log to Firestore, logging locally:', err);
      }

      const local = getLocalAuditLogs();
      local.unshift(logEntry);
      saveLocalAuditLogs(local.slice(0, 100)); // Keep last 100

      return true;
    } catch (err) {
      console.error('Failed to log audit action:', err);
      return false;
    }
  },

  /**
   * Fetch recent audit logs
   */
  async getAuditLogs(maxCount: number = 50): Promise<AuditLog[]> {
    const list: AuditLog[] = [];
    try {
      const q = query(collection(db, 'auditLogs'), orderBy('timestamp', 'desc'), limit(maxCount));
      const snap = await getDocs(q);
      snap.forEach((d) => {
        if (!['log_01', 'log_02', 'log_03', 'log_04'].includes(d.id)) {
          list.push({ ...d.data(), id: d.id } as AuditLog);
        }
      });
    } catch (err) {
      console.warn('Could not read audit logs from Firestore, reading local:', err);
    }

    const local = getLocalAuditLogs();
    for (const item of local) {
      if (!list.some((existing) => existing.id === item.id)) {
        list.push(item);
      }
    }

    return list.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  },
};
